import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import { NOTIFICATIONS_STATUS, useNotifications } from "../context/NotificationsContext";
import { getLoginNotifications, toAnnouncement } from "../helpers/notificationsHelper";
import {
  dismissFallbackNotices,
  getAnnouncementSessionKey,
  hasShownAnnouncement,
  isFallbackNoticeDismissed,
  markAnnouncementShown,
} from "../helpers/announcementSession";
import { WHATSAPP_CHAT_URL } from "../config/whatsappContact";
import AnnouncementModal from "./AnnouncementModal";
import { canViewCoordinatorPortal } from "./PortalDashboard";

const NEW_ANNOUNCEMENT_CONTACT = {
  label: "Caso tenha dúvidas sobre o funcionamento dessa função, não hesite em",
  text: "entrar em contato conosco",
  suffix: ".",
  href: WHATSAPP_CHAT_URL,
  external: true,
};

export const ADVANCED_SEARCH_NEWS =
  "Também está disponível a nova Busca Avançada: é possível pesquisar clientes pelo nome combinado com outros critérios, como CPF/CNPJ, número do processo, e-mail, cidade, estado (UF), CEP, tipo de caso, situação e tipo de cliente. Para acessar, clique na lupa no topo do portal e selecione “Busca avançada”.";

export const COORDINATOR_GLOBAL_SEARCH_NEWS =
  "Para coordenadores: o Portal do Coordenador agora tem uma barra de busca no topo da página. Basta digitar o nome, CPF, CNPJ, e-mail, CEP, número do processo (MAT-…) ou MS ID para encontrar qualquer cliente ou processo dos advogados colaboradores. As sugestões aparecem enquanto você digita, e “Ver todos os resultados” abre a lista completa.";

// Fallback steps carry the same key/version as their DB notification so a
// "don't show again" made offline is keyed like the server one.
export const NEW_ANNOUNCEMENT = {
  key: "client-portal-documents-update",
  version: 1,
  title: "ATUALIZAÇÃO NO PORTAL DE CLIENTES",
  body: [
    "Os clientes agora podem ver quais documentos já foram vinculados às suas matrículas. Caso algum documento esteja pendente, eles podem carregar o mesmo de imediato, utilizando o próprio portal, diminuindo a necessidade de atendimento presencial em seu escritório.",
    "Os documentos carregados podem ser conferidos no Canal do Advogado, na matrícula de cada cliente.",
    NEW_ANNOUNCEMENT_CONTACT,
  ],
};

export const ANNOUNCEMENT = {
  key: "mariana-client-committee",
  version: 1,
  title: "Atenção clientes e advogados colaboradores:",
  body: [
    "Sabemos que as notícias recentes sobre o Comitê de Clientes podem ter gerado dúvidas ou preocupações.",
    "Por isso, queremos deixar uma mensagem clara:",
    { text: "O caso inglês Mariana continua.", strong: true },
    "A decisão do Comitê de Clientes não encerra os processos de nossos clientes. O PGMBM continua representando centenas de milhares de autores, e o processo segue avançando para a Fase 2, etapa em que os diferentes tipos de danos e os pedidos de indenização serão avaliados.",
    "O julgamento da Fase 2 está atualmente previsto para começar em abril de 2027.",
    "Enquanto isso, nossas equipes continuam trabalhando na preparação necessária para essa próxima etapa, incluindo documentos e provas, casos modelos, provas periciais, questões jurídicas e a avaliação dos diferentes tipos de danos.",
    "Também foi recentemente garantido um financiamento adicional de US$ 150 milhões para apoiar o trabalho necessário para a próxima fase do processo.",
    "Se você é cliente ou advogado colaborador do Pogust Goodhead (“PGMBM”), não precisa tomar nenhuma medida em razão da decisão do Comitê de Clientes.",
    "Continue acompanhando nossas comunicações oficiais e mantenha seus dados de contato atualizados.",
    "Seguimos focados em proteger os interesses de nossos clientes e avançar com o processo.",
  ],
};

export const SECOND_ANNOUNCEMENT = {
  key: "pgmbm-official-representation",
  version: 1,
  title: "Atenção",
  body: [
    "O Pogust Goodhead (PGMBM) continua sendo o escritório oficial que representa os clientes na Ação Inglesa de Mariana.",
    "Para proteger a representação dos seus clientes:",
    {
      list: [
        "Seja cauteloso antes de orientar qualquer decisão.",
        "Cliente recebeu documento para assinar? Por favor, oriente-o a não assinar sem nos consultar.",
        "Cliente foi procurado para mudar de representação? Por favor, fale conosco imediatamente.",
        "Cliente recebeu proposta de transferência para outro escritório? Por favor, fale conosco imediatamente.",
        "Não baseie orientações em listas ou informações não oficiais do PGMBM.",
      ],
    },
    "Em caso de dúvida, sempre utilize nossos canais oficiais para advogados(as) colaboradores(as).",
    {
      label: "Email:",
      text: "advogadoparceiro@pogustgoodhead.com",
      href: "mailto:advogadoparceiro@pogustgoodhead.com",
    },
    {
      label: "Instagram:",
      text: "@pgmbmbrasil",
      href: "https://www.instagram.com/pgmbmbrasil/",
      external: true,
    },
    { text: "Juntos, somos mais fortes!", strong: true },
  ],
};

export const CONTACT_QUALITY_ANNOUNCEMENT = {
  key: "contact-quality",
  version: 1,
  title: "Novo: Qualidade do contato dos seus clientes",
  body: [
    "Na lista de clientes, agora você vê se o email e o telefone de cada cliente estão em ordem. Cada cliente recebe uma cor:",
    {
      list: [
        { text: "Verde – email e telefone corretos. Nenhuma ação necessária.", tone: "green" },
        { text: "Âmbar – o email ou o telefone precisa de atenção (está faltando, está duplicado com outro cliente ou parece incorreto).", tone: "amber" },
        { text: "Vermelho – email e telefone precisam ser corrigidos com urgência.", tone: "red" },
        { text: "Não avaliado – ainda não há avaliação para este cliente.", tone: "neutral" },
      ],
    },
    "Passe o mouse (ou toque) sobre o indicador para ver o motivo. Use o filtro \"Qualidade do contato\" para listar, por exemplo, só os clientes em vermelho.",
    "Por que isso importa: sem email e telefone válidos não conseguimos avisar o cliente sobre prazos, documentos pendentes e o andamento do processo. Se você tiver dados atualizados, corrija-os na ficha do cliente.",
  ],
};

export const ANNOUNCEMENTS = [NEW_ANNOUNCEMENT, ANNOUNCEMENT, SECOND_ANNOUNCEMENT];

// Hardcoded steps used only when GET /notifications fails, minus the ones this
// user ticked "Não mostrar novamente" for while offline.
export const buildFallbackAnnouncements = (features, user) => [
  ...(features?.contactQuality === true ? [CONTACT_QUALITY_ANNOUNCEMENT] : []),
  buildNewAnnouncement(features, user),
  ANNOUNCEMENT,
  SECOND_ANNOUNCEMENT,
].filter((notice) => !isFallbackNoticeDismissed(user, notice));

// Feature news only advertises what is switched on for this user; the coordinator item
// uses the same capability check as the CoCounsel/Coordinator switch.
export const buildNewAnnouncement = (features, user) => {
  const news = [];
  if (features?.advancedClientSearch === true) news.push(ADVANCED_SEARCH_NEWS);
  if (
    features?.coordinatorGlobalSearch === true
    && canViewCoordinatorPortal(user?.capabilities)
  ) {
    news.push(COORDINATOR_GLOBAL_SEARCH_NEWS);
  }
  if (!news.length) return NEW_ANNOUNCEMENT;
  const body = NEW_ANNOUNCEMENT.body.slice(0, -1);
  return { ...NEW_ANNOUNCEMENT, body: [...body, ...news, NEW_ANNOUNCEMENT_CONTACT] };
};

const AnnouncementGate = () => {
  const { tokens, user } = useAuth();
  const { features, ready: flagsReady = true } = useFeatureFlags();
  const {
    notifications,
    status: notificationsStatus,
    settled: notificationsSettled,
    markRead,
    dismiss,
  } = useNotifications();
  const handledSessionKey = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  // { source: "server", items } from GET /notifications, or { source: "fallback", items }.
  const [steps, setSteps] = useState(null);
  // Per-step "don't show this message again", keyed by stepKey (server id or fallback key).
  const [doNotShowAgain, setDoNotShowAgain] = useState({});
  const sessionKey = getAnnouncementSessionKey(user);

  useEffect(() => {
    const isAuthenticated = Boolean(tokens?.idToken);

    if (!isAuthenticated) {
      setIsOpen(false);
      setSteps(null);
      handledSessionKey.current = null;
      return;
    }

    // Wait for /features and /notifications so the first step is the right one.
    if (!flagsReady || !notificationsSettled) return;
    if (handledSessionKey.current === sessionKey) return;
    handledSessionKey.current = sessionKey;

    if (hasShownAnnouncement(user)) {
      setIsOpen(false);
      return;
    }

    if (notificationsStatus === NOTIFICATIONS_STATUS.ERROR) {
      const items = buildFallbackAnnouncements(features, user);
      if (!items.length) {
        setIsOpen(false);
        return;
      }
      setSteps({ source: "fallback", items });
    } else {
      const items = getLoginNotifications(notifications);
      if (!items.length) {
        setIsOpen(false);
        return;
      }
      setSteps({ source: "server", items });
    }

    setStepIndex(0);
    setDoNotShowAgain({});
    setIsOpen(true);
  }, [
    features,
    flagsReady,
    notifications,
    notificationsSettled,
    notificationsStatus,
    sessionKey,
    tokens?.idToken,
    user,
  ]);

  const isServer = steps?.source === "server";
  const stepCount = steps?.items.length || 0;
  const isLastStep = stepIndex >= stepCount - 1;
  const snapshot = steps?.items[stepIndex];
  // Prefer the live copy so a language switch while open shows the new translation.
  const currentNotification = isServer
    ? notifications.find((item) => item.id === snapshot?.id) || snapshot
    : null;
  const currentId = isOpen && isServer ? snapshot?.id : null;
  const stepKey = useCallback((item) => (isServer ? item?.id : item?.key), [isServer]);
  const currentStepKey = stepKey(snapshot);
  const currentChecked = Boolean(currentStepKey && doNotShowAgain[currentStepKey]);
  const setCurrentChecked = useCallback(
    (checked) => {
      if (!currentStepKey) return;
      setDoNotShowAgain((current) => ({ ...current, [currentStepKey]: checked }));
    },
    [currentStepKey],
  );

  useEffect(() => {
    if (currentId) markRead([currentId]);
  }, [currentId, markRead]);

  const handleNext = useCallback(() => {
    setStepIndex((current) => Math.min(current + 1, stepCount - 1));
  }, [stepCount]);

  const handleDismiss = useCallback(() => {
    markAnnouncementShown(user);
    const checkedItems = (steps?.items || []).filter((item) => doNotShowAgain[stepKey(item)]);
    if (checkedItems.length) {
      if (isServer) dismiss(checkedItems.map((item) => item.id));
      else dismissFallbackNotices(user, checkedItems);
    }
    setIsOpen(false);
  }, [dismiss, doNotShowAgain, isServer, stepKey, steps, user]);

  if (!isOpen || !snapshot) return null;

  if (!isServer) {
    return (
      <AnnouncementModal
        announcement={snapshot}
        stepIndex={stepIndex}
        stepCount={stepCount}
        dismissible={isLastStep}
        doNotShowAgain={{ checked: currentChecked, onChange: setCurrentChecked }}
        onNext={handleNext}
        onDismiss={handleDismiss}
      />
    );
  }

  return (
    <AnnouncementModal
      announcement={toAnnouncement(currentNotification)}
      stepIndex={stepIndex}
      stepCount={stepCount}
      dismissible={isLastStep}
      acknowledgeLabel={currentNotification.acknowledgeLabel}
      closeLabel={currentNotification.closeLabel}
      doNotShowAgain={{ checked: currentChecked, onChange: setCurrentChecked }}
      onNext={handleNext}
      onDismiss={handleDismiss}
    />
  );
};

export default AnnouncementGate;
